import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeDollarSign, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { cadStr, dateTime, shortDate } from "@/lib/pricing";
import { StripePricesCard } from "@/components/admin/StripePricesCard";

const fmt = (iso) => (iso && !Number.isNaN(Date.parse(iso)) ? dateTime(iso) : iso || "—");

function Activation({ a, version }) {
  return (
    <div className="pf-card p-5 mt-6 grid sm:grid-cols-4 gap-4" data-testid="admin-pricing-activation">
      <div><div className="text-[11px] text-slate-500">Active version</div><div className="font-display text-xl" data-testid="admin-pricing-version">{version}</div></div>
      <div><div className="text-[11px] text-slate-500">New pricing live here</div><div className="font-display text-xl" data-testid="admin-pricing-final-active">{a.final_active ? "Yes" : "No"}</div></div>
      <div><div className="text-[11px] text-slate-500">Effective from</div><div className="text-sm font-medium">{fmt(a.effective_at)}</div></div>
      <div>
        <div className="text-[11px] text-slate-500">Production approval</div>
        <div className={`text-sm font-semibold ${a.production_approved ? "text-emerald-700" : "text-amber-700"}`} data-testid="admin-pricing-prod-approval">
          {a.production_approved ? "Approved" : "Not approved — production stays on legacy pricing"}
        </div>
      </div>
    </div>
  );
}

function PlansTable({ pricing }) {
  return (
    <div className="pf-card overflow-hidden mt-6" data-testid="admin-pricing-plans">
      <table className="w-full text-sm">
        <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
          <tr><th className="px-5 py-3">Plan (internal id)</th><th className="px-5 py-3">Monthly</th><th className="px-5 py-3">Annual</th><th className="px-5 py-3">Fee</th><th className="px-5 py-3">Included</th></tr>
        </thead>
        <tbody>
          {pricing.plans.map((p) => (
            <tr key={p.id} className="border-t border-white/60" data-testid={`admin-pricing-plan-${p.id}`}>
              <td className="px-5 py-3 font-medium">{p.name} <span className="text-xs text-slate-400 font-mono">({p.id})</span></td>
              <td className="px-5 py-3 font-mono">{cadStr(p.monthly_cad)}</td>
              <td className="px-5 py-3 font-mono">{cadStr(p.annual_cad)}</td>
              <td className="px-5 py-3 font-mono">{p.fee_pct}% / min {cadStr(p.fee_minimum_cad)}</td>
              <td className="px-5 py-3 text-xs text-slate-600">{p.storefronts_included} storefront(s) · {p.provinces_included_per_storefront} province(s) · {p.analytics} analytics{p.ai_included ? " · AI + MCP" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-5 py-3 text-xs text-slate-600 border-t border-white/60" data-testid="admin-pricing-cashback">
        Shopper cashback: <strong>{pricing.shopper_cashback?.standard_rate_display || "0.75% ≤ CA$200 · 1% above"}</strong> · Gift card redemption from {cadStr(pricing.gift_card_min_redemption_cad)}
      </div>
    </div>
  );
}

function MerchantsTable({ rows }) {
  return (
    <div className="pf-card overflow-x-auto mt-6" data-testid="admin-pricing-merchants">
      <table className="w-full text-sm">
        <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
          <tr><th className="px-4 py-3">Merchant</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Billing</th><th className="px-4 py-3">New fees from</th><th className="px-4 py-3">Subscription price</th><th className="px-4 py-3">Scheduled change</th></tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.store_id} className="border-t border-white/60" data-testid={`admin-pricing-merchant-${m.store_id}`}>
              <td className="px-4 py-3 font-medium">{m.name}{m.test_bypass && <span className="ml-1 text-[10px] text-amber-700">TEST</span>}</td>
              <td className="px-4 py-3">{m.plan_label}</td>
              <td className="px-4 py-3 text-xs">{m.billing_interval || "—"}</td>
              <td className="px-4 py-3 text-xs">{fmt(m.fees_new_from)}</td>
              <td className="px-4 py-3 text-xs" data-testid={`admin-pricing-merchant-${m.store_id}-price`}>{m.grandfathered ? (m.grandfathered.new_customers_price_cad !== m.grandfathered.current_price_cad
                ? `${cadStr(m.grandfathered.current_price_cad)} → ${cadStr(m.grandfathered.new_customers_price_cad)}/${m.grandfathered.period} at renewal${m.grandfathered.next_renewal_at ? ` (${shortDate(m.grandfathered.next_renewal_at)})` : ""}`
                : `${cadStr(m.grandfathered.current_price_cad)}/${m.grandfathered.period}`) : "—"}</td>
              <td className="px-4 py-3 text-xs">{m.pending_plan_change ? `${m.pending_plan_change.to_plan} on ${fmt(m.pending_plan_change.effective_at)}` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPricing() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/pricing/overview").then((r) => setD(r.data)).catch(() => setD(null)), []);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    try {
      setBusy(true);
      const { data } = await api.post("/admin/pricing/run-maintenance");
      toast.success(`Done · ${data.downgrades_applied} downgrade(s) applied, ${data.allowances?.resynced ?? 0} allowance resync(s)`);
      load();
    } catch (err) {
      toast.error(err?.response?.status === 403 ? "Super admin only" : "Maintenance run failed");
    } finally {
      setBusy(false);
    }
  };
  if (!d) return <div className="text-sm text-slate-500" data-testid="admin-pricing-loading">Loading pricing…</div>;
  return (
    <div data-testid="admin-pricing">
      <div className="pf-chip pf-chip-lav"><BadgeDollarSign className="w-3 h-3" /> Pricing</div>
      <div className="flex items-end justify-between flex-wrap gap-3 mt-3">
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-slate-900">Merchant pricing</h2>
        <button onClick={runNow} disabled={busy} data-testid="admin-pricing-run-maintenance"
                className="pf-btn-glass px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run plan maintenance now
        </button>
      </div>
      {!d.activation.production_approved && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 inline-flex items-center gap-2" data-testid="admin-pricing-preview-warning">
          <ShieldAlert className="w-4 h-4" /> Preview only. Production keeps legacy pricing until PRICING_2026_10_PRODUCTION_APPROVED is set after sign-off.
        </div>
      )}
      <Activation a={d.activation} version={d.pricing.pricing_version} />
      <StripePricesCard />
      <div className="mt-6 grid sm:grid-cols-4 gap-4" data-testid="admin-pricing-counts">
        {["basic", "starter", "growth"].map((id) => (
          <div key={id} className="pf-card p-4" data-testid={`admin-pricing-count-${id}`}>
            <div className="text-[11px] text-slate-500">{d.pricing.plans.find((p) => p.id === id)?.name || id} merchants</div>
            <div className="font-display text-2xl">{d.plan_counts[id] || 0}</div>
          </div>
        ))}
        <div className="pf-card p-4" data-testid="admin-pricing-final-sales">
          <div className="text-[11px] text-slate-500">Verified sales on new pricing</div>
          <div className="font-display text-2xl">{d.verified_sales_on_final_pricing}</div>
        </div>
      </div>
      <PlansTable pricing={d.pricing} />
      <MerchantsTable rows={d.merchants} />
    </div>
  );
}
